import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { first, map, of, switchMap } from 'rxjs';

import { DatasetColumn } from '@blaze-shared/schema-domain';
import { DialogService } from '@blaze-shared/shared-feature';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { SelectOption } from '@blaze-shared/shared-util';

import { TableBaseEditorComponent } from '../../elements/table/inline-editor/base-editor/base-editor.component';
import { InlineEditorConfig } from '../../elements/table/inline-editor/base-editor/inline-editor.types';
import {
  MenuEditModeColumnFormats,
  MenuEditModeDataTypes,
} from '../../utils/elements/table-inline-editor.utils';

type BulkEditComponentAction = 'cancel' | 'continue';

@Component({
  selector: 'blaze-bulk-edit',
  templateUrl: './bulk-edit.component.html',
  styleUrls: ['./bulk-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex h-full max-h-[90vh]' },
  standalone: false,
})
export class BulkEditComponent implements OnInit, OnDestroy {
  private dialogRef = inject<MatDialogRef<BulkEditComponent>>(MatDialogRef);
  private cdr = inject(ChangeDetectorRef);
  private dialogService = inject(DialogService);

  dialogData = inject<{ columns: DatasetColumn[] }>(MAT_DIALOG_DATA);

  @ViewChildren(TableBaseEditorComponent) editors!: QueryList<TableBaseEditorComponent>;

  availableColumnsLength = 0;
  unusedColumnsLength = 0;
  formGroup = new UntypedFormGroup(
    {
      array: new UntypedFormArray([]),
    },
    bulkEditValidatorFn,
  );

  get columns(): DatasetColumn[] {
    return this.dialogData.columns;
  }

  get isSaveDisabled(): boolean {
    return !this.formGroup?.valid || false;
  }

  get bulkEditArrayControls(): UntypedFormGroup[] {
    return this.arrayForm.controls as UntypedFormGroup[];
  }

  get formGroupArrayLength(): number {
    return this.arrayForm.controls.length;
  }

  get arrayForm(): UntypedFormArray {
    return this.formGroup.get('array') as UntypedFormArray;
  }

  constructor() {
    this.addFormGroup();
    this.availableColumnsLength = this.getAvailableColumns().length;
    this.unusedColumnsLength = this.getUnusedColumns().length;
  }

  ngOnInit(): void {
    this.dialogService.setDialogOpened(true);
  }

  ngOnDestroy(): void {
    this.dialogService.setDialogOpened(false);
  }

  getAvailableColumns(): SelectOption<string>[] {
    return this.dialogData.columns
      .filter((col: DatasetColumn) => {
        return !col.isComputed && !col.isInline && !col.isInternal;
      })
      .map((col: DatasetColumn) => ({ label: col.name, value: col.name }));
  }

  getUnusedColumns(): SelectOption<string>[] {
    return this.getAvailableColumns().filter((col: SelectOption<string>) => {
      return !this.arrayForm.controls.some((group) => {
        return group.get('name')?.value === col.value;
      });
    });
  }

  addFormGroup(): void {
    this.arrayForm.push(this.createFormGroup());
  }

  removeFormGroup(index: number): void {
    this.arrayForm.removeAt(index);
    this.unusedColumnsLength = this.getUnusedColumns().length;
  }

  setValueForControl(name: string, index: number, value: any): void {
    this.arrayForm.at(index).patchValue({ value: value });
  }

  getConfigForField(name: string): InlineEditorConfig {
    return {
      columnSchema: this.columns.find((col) => col.name === name),
      inputStyles: {},
    };
  }

  getEditModeForField(name: string): 'menu' | 'inline-editor' {
    const col = this.columns.find((col) => col.name === name);

    const dataType = col?.dataType;
    const format = col?.format?.formatType;

    return (dataType && MenuEditModeDataTypes.includes(dataType)) ||
      (format && MenuEditModeColumnFormats.includes(format))
      ? 'menu'
      : 'inline-editor';
  }

  changeEditorConfig(index: number, fieldName: string): void {
    of(this.editors.get(index))
      .pipe(
        switchMap((tpl) =>
          tpl
            ? of(tpl)
            : this.editors.changes.pipe(
                first(),
                map(() => this.editors.get(index)),
              ),
        ),
      )
      .subscribe((tpl) => {
        tpl?.setInitialConfig(null, undefined, {
          columnSchema: this.columns.find((col) => col.name === fieldName),
        });
        safeDetectChanges(this.cdr);
      });
    this.unusedColumnsLength = this.getUnusedColumns().length;
  }

  unusedColumnsToChange(name?: string): SelectOption<string, string>[] {
    const result = [...this.getUnusedColumns()];

    if (name) {
      result.unshift({ label: name, value: name });
    }

    return result;
  }

  action(action: BulkEditComponentAction): void {
    if (action === 'continue') {
      return this.dialogRef.close({
        payload: this.formGroup.value.array
          .map(({ name, value }: { name: string; value: any }) => ({ [name]: value }))
          .reduce(
            (acc: Record<string, any>, curr: Record<string, any>) => Object.assign(acc, curr),
            {},
          ),
      });
    }
    return this.dialogRef.close(action);
  }

  private createFormGroup(): UntypedFormGroup {
    return new UntypedFormGroup({
      name: new UntypedFormControl(),
      value: new UntypedFormControl(),
    });
  }
}

function bulkEditValidatorFn(control: AbstractControl<any, any>): Record<string, boolean> | null {
  let result: Record<string, boolean> | null = {};

  const arrayControls = (control.get('array') as UntypedFormArray)?.controls;

  if (!arrayControls.length) {
    result['required'] = true;
  } else if (
    arrayControls.some((group) => {
      return !(group as UntypedFormGroup).get('name')?.value;
    })
  ) {
    result['required'] = true;
  } else {
    result = null;
  }

  return result;
}
